import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { hashContent } from '@/lib/knowledge/extract-document';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';
import {
  type ArticleJobRequest,
  type ArticleReviewJob,
  articleJobService,
  isArticleJobActive,
} from '@/services/data/ArticleJobService';

export type { ArticleReviewJob } from '@/services/data/ArticleJobService';
export type ArticleJobStartInput = { topicId: string } & (
  | { operation: 'draft'; expectedArtifactId: null }
  | {
      operation: 'recheck' | 'revise' | 'factcheck';
      documentId: string;
      markdown: string;
      feedback?: string;
      /** `recheck` only: run the editorial critique again although the text has not changed. */
      refreshCritique?: boolean;
    }
  | {
      operation: 'address';
      documentId: string;
      markdown: string;
      scope?: 'all' | 'claims';
    }
);
export const DEFAULT_ARTICLE_IMPROVEMENT =
  'Address the current critique findings while preserving the brief, sources and brand voice; remove unsupported specificity.';

/** Keep uncertain network retries idempotent across repeated clicks and surfaces. */
const requestKeys = new Map<string, string>();
const inFlight = new Map<string, Promise<{ jobId: string; existing?: boolean }>>();
registerWorkspaceReset('article-job-requests', () => {
  requestKeys.clear();
  inFlight.clear();
});

export function useStartArticleJob(workspaceId: string | null) {
  const client = useQueryClient();
  return useMutation({
    onMutate: () => ({ workspaceId }),
    mutationFn: async (input: ArticleJobStartInput) => {
      if (!workspaceId) throw new Error('Select the article workspace first.');
      const feedback =
        input.operation === 'revise'
          ? input.feedback?.trim() || DEFAULT_ARTICLE_IMPROVEMENT
          : undefined;
      if (feedback && feedback.length > 4000)
        throw new Error('Keep improvement feedback under 4,000 characters.');
      const fields =
        input.operation === 'draft'
          ? { operation: 'draft' as const, expectedArtifactId: null }
          : {
              operation: input.operation,
              documentId: input.documentId,
              contentHash: await hashContent(input.markdown),
              ...(feedback ? { feedback } : {}),
              ...(input.operation === 'recheck' && input.refreshCritique
                ? { refreshCritique: true }
                : {}),
              ...(input.operation === 'address' && input.scope === 'claims'
                ? { scope: 'claims' as const }
                : {}),
            };
      const fingerprint = JSON.stringify({ workspaceId, topicId: input.topicId, ...fields });
      const existing = inFlight.get(fingerprint);
      if (existing) return existing;
      const requestKey = requestKeys.get(fingerprint) ?? crypto.randomUUID();
      requestKeys.set(fingerprint, requestKey);
      const pending = articleJobService.start({
        workspaceId,
        topicId: input.topicId,
        ...fields,
        requestKey,
      } as ArticleJobRequest);
      inFlight.set(fingerprint, pending);
      try {
        const result = await pending;
        if (requestKeys.get(fingerprint) === requestKey) requestKeys.delete(fingerprint);
        return result;
      } finally {
        if (inFlight.get(fingerprint) === pending) inFlight.delete(fingerprint);
      }
    },
    onSuccess: async (_result, input, context) => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ['article-review-job', context?.workspaceId, input.topicId],
        }),
        client.invalidateQueries({ queryKey: ['article-workspace-jobs', context?.workspaceId] }),
      ]);
    },
  });
}

export function useStopArticleJob(workspaceId: string | null) {
  const client = useQueryClient();
  return useMutation({
    onMutate: () => ({ workspaceId }),
    mutationFn: ({ jobId }: { jobId: string; topicId: string }) => articleJobService.stop(jobId),
    onSuccess: async (_result, input, context) => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ['article-review-job', context?.workspaceId, input.topicId],
        }),
        client.invalidateQueries({ queryKey: ['article-workspace-jobs', context?.workspaceId] }),
        client.invalidateQueries({
          queryKey: ['article-job-receipt', context?.workspaceId, input.jobId],
        }),
      ]);
    },
  });
}

/** Make a saved revision the topic's current article; every surface showing the article refreshes. */
export function usePromoteArticleRevision(workspaceId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { topicId: string; messageId: string; documentId: string }) =>
      articleJobService.promote(input),
    onSuccess: async (_result, input) => {
      await Promise.all(
        [
          ['article-review-job', workspaceId, input.topicId],
          ['article-workspace-jobs', workspaceId],
          ['editorial-map', workspaceId],
          ['editorial-article-trail'],
          ['editorial-article-scores'],
          ['document-output'],
          ['documents'],
          ['desk'],
          ['desk-workspace'],
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
    },
  });
}

function useArticleJobResults(
  workspaceId: string | null,
  jobs: ArticleReviewJob[],
  sourceMessageId?: string | null,
) {
  const client = useQueryClient();
  const observed = useRef(new Set<string>());
  useEffect(() => {
    for (const job of jobs) {
      const terminal = !isArticleJobActive(job);
      if (!job.resultMessageId && !terminal) continue;
      const receipt = JSON.stringify([
        workspaceId,
        job.id,
        job.resultMessageId,
        job.resultDocumentId,
        terminal ? job.status : 'artifact',
      ]);
      if (observed.current.has(receipt)) continue;
      observed.current.add(receipt);
      // A result can be filed before its critique completes. Read must become
      // available at that first pointer, not wait for a terminal job status.
      for (const queryKey of [
        ['editorial-map', workspaceId],
        [
          'editorial-article-trail',
          ...(job.resultMessageId || sourceMessageId
            ? [job.resultMessageId || sourceMessageId]
            : []),
        ],
        ['editorial-article-scores'],
        ['document-output'],
        ['documents'],
        ['desk'],
        ['desk-workspace'],
      ])
        void client.invalidateQueries({ queryKey });
    }
  }, [client, jobs, sourceMessageId, workspaceId]);
}

function useArticleJobQuery(workspaceId: string | null, topicId: string) {
  return useQuery({
    queryKey: ['article-review-job', workspaceId, topicId],
    enabled: Boolean(workspaceId && topicId),
    queryFn: () => articleJobService.get(workspaceId!, topicId),
    refetchInterval: (q) => (isArticleJobActive(q.state.data?.job) ? 1500 : false),
  });
}

/**
 * Read-only view of one topic's latest job, for a surface that only shows its state (a header
 * badge). Shares `useArticleReviewJob`'s cache entry and, unlike it, never invalidates the article
 * queries when a job settles: the review rail beside it already owns that.
 */
export function useArticleTopicJob(workspaceId: string | null, topicId: string) {
  return useArticleJobQuery(workspaceId, topicId).data?.job ?? null;
}

/** One topic's durable job, including all recorded step findings. Unwritten topics are observable. */
export function useArticleReviewJob(
  workspaceId: string | null,
  topicId: string,
  messageId: string | null,
) {
  const query = useArticleJobQuery(workspaceId, topicId);
  const job = query.data?.job ?? null;
  useArticleJobResults(workspaceId, job ? [job] : [], messageId);
  const start = useStartArticleJob(workspaceId);
  const stop = useStopArticleJob(workspaceId);
  const promote = usePromoteArticleRevision(workspaceId);
  return {
    job,
    active: isArticleJobActive(job),
    start,
    stop,
    promote,
    error: query.error,
    isPending: query.isPending,
  };
}

/** Lightweight workspace projection; full findings are fetched only when a topic opens. */
export function useWorkspaceArticleJobs(workspaceId: string | null) {
  const query = useQuery({
    queryKey: ['article-workspace-jobs', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: () => articleJobService.list(workspaceId!),
    refetchInterval: (q) => (q.state.data?.jobs.some(isArticleJobActive) ? 1500 : false),
  });
  const jobs = query.data?.jobs ?? [];
  useArticleJobResults(workspaceId, jobs);
  return {
    jobs,
    truncated: query.data?.truncated ?? false,
    isPending: query.isPending,
    error: query.error,
  };
}
